import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit2, ChevronLeft, ChevronRight, Power, Building2, Bell } from 'lucide-react';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { toast } from '../ui/Toast';
import { patientsApi } from '../../api/patients';
import type { PaginatedPatients, PatientListItem } from '../../types/patient';
import { DOCUMENT_TYPE_LABELS } from '../../types/patient';
import { formatPhone } from '../../utils/format';

interface PatientTableProps {
  data: PaginatedPatients;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
  isLoading?: boolean;
}

export function PatientTable({ data, onPageChange, onRefresh, isLoading = false }: PatientTableProps) {
  const navigate = useNavigate();
  const { data: patients, meta } = data;

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    patient: PatientListItem | null;
    action: 'activate' | 'deactivate';
  }>({ isOpen: false, patient: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Helper para obtener el estado en el centro actual (con fallback a isActive)
  const getIsActiveInSite = (patient: PatientListItem): boolean => {
    return patient.isActiveInCurrentSite ?? patient.isActive;
  };

  const handleToggleStatus = (patient: PatientListItem) => {
    const isActiveInSite = getIsActiveInSite(patient);
    setConfirmDialog({
      isOpen: true,
      patient,
      action: isActiveInSite ? 'deactivate' : 'activate',
    });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.patient) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await patientsApi.delete(confirmDialog.patient.id);
        toast.success('Paciente desactivado', `${confirmDialog.patient.fullName} ha sido desactivado en este centro.`);
      } else {
        await patientsApi.reactivate(confirmDialog.patient.id);
        toast.success('Paciente activado', `${confirmDialog.patient.fullName} ha sido reactivado en este centro.`);
      }
      onRefresh();
    } catch (error: any) {
      toast.error('Error', error.message || 'No se pudo cambiar el estado');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, patient: null, action: 'deactivate' });
    }
  };

  if (patients.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <div className="text-gray-400 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No hay pacientes</h3>
        <p className="text-gray-500">Crea tu primer paciente o vincula uno existente de otro centro.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Paciente</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Documento</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contacto</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Centros</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {patients.map((patient) => {
                const isActiveInSite = getIsActiveInSite(patient);
                const isInactiveGlobal = !patient.isActive;
                const linkedSitesCount = patient.linkedSitesCount ?? 1;
                
                return (
                  <tr 
                    key={patient.id} 
                    className={`hover:bg-gray-50 ${!isActiveInSite && !isInactiveGlobal ? 'bg-amber-50/30' : ''} ${isInactiveGlobal ? 'bg-gray-50/50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button onClick={() => navigate(`/pacientes/${patient.id}`)} className="text-left group">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                            isInactiveGlobal 
                              ? 'bg-gray-200 text-gray-500' 
                              : !isActiveInSite 
                                ? 'bg-amber-100 text-amber-600' 
                                : 'bg-blue-100 text-blue-600'
                          }`}>
                            {patient.fullName.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
                          </div>
                          <div>
                            <div className={`font-medium group-hover:text-blue-600 group-hover:underline ${
                              isInactiveGlobal ? 'text-gray-500' : !isActiveInSite ? 'text-amber-900' : 'text-gray-900'
                            }`}>
                              {patient.fullName}
                            </div>
                            {!isActiveInSite && !isInactiveGlobal && (
                              <span className="text-xs text-amber-600">Inactivo en este centro</span>
                            )}
                            {isInactiveGlobal && (
                              <span className="text-xs text-gray-500">Inactivo global</span>
                            )}
                          </div>
                        </div>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{patient.documentNumber}</div>
                      <div className="text-xs text-gray-500">{DOCUMENT_TYPE_LABELS[patient.documentType]}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="text-sm text-gray-900">{formatPhone(patient.phonePrimary)}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[200px]">{patient.email}</div>
                        </div>
                        {patient.hasPendingReminders && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100"
                            title="Paciente con recordatorios pendientes"
                          >
                            <Bell className="h-3 w-3" />
                            Rec.
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span className={`text-sm ${linkedSitesCount > 1 ? 'text-blue-600 font-medium' : 'text-gray-600'}`}>
                          {linkedSitesCount}
                        </span>
                        {linkedSitesCount > 1 && (
                          <span className="text-xs text-gray-500">centros</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleToggleStatus(patient)}
                        disabled={isInactiveGlobal}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          isInactiveGlobal 
                            ? 'bg-gray-200 cursor-not-allowed' 
                            : isActiveInSite 
                              ? 'bg-blue-600' 
                              : 'bg-amber-400'
                        }`}
                        role="switch"
                        aria-checked={isActiveInSite}
                        title={
                          isInactiveGlobal 
                            ? 'Paciente inactivo globalmente' 
                            : isActiveInSite 
                              ? 'Desactivar en este centro' 
                              : 'Activar en este centro'
                        }
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          isActiveInSite ? 'translate-x-5' : 'translate-x-0'
                        }`}>
                          <Power className={`h-3 w-3 absolute top-1 left-1 ${
                            isInactiveGlobal 
                              ? 'text-gray-400' 
                              : isActiveInSite 
                                ? 'text-blue-600' 
                                : 'text-amber-500'
                          }`} />
                        </span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => navigate(`/pacientes/${patient.id}`)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Ver detalle">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button onClick={() => navigate(`/pacientes/${patient.id}/editar`)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded" title="Editar">
                          <Edit2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {meta.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Mostrando {(meta.page - 1) * meta.limit + 1} a {Math.min(meta.page * meta.limit, meta.total)} de {meta.total}
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => onPageChange(meta.page - 1)} disabled={!meta.hasPrevPage || isLoading} className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-700">Página {meta.page} de {meta.totalPages}</span>
              <button onClick={() => onPageChange(meta.page + 1)} disabled={!meta.hasNextPage || isLoading} className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => !isProcessing && setConfirmDialog({ isOpen: false, patient: null, action: 'deactivate' })}
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={confirmDialog.action === 'deactivate' ? '¿Desactivar en este centro?' : '¿Activar en este centro?'}
        message={confirmDialog.action === 'deactivate' 
          ? `El paciente "${confirmDialog.patient?.fullName}" será desactivado en este centro. Seguirá disponible en otros centros donde esté vinculado.`
          : `El paciente "${confirmDialog.patient?.fullName}" será reactivado en este centro.`}
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </>
  );
}
